import { en, type Catalog, type MessageKey } from "./en.ts";
import { DEFAULT_LOCALE, isLocale, LOCALES, matchLocale, type Locale } from "./locales.ts";

export type { Locale, MessageKey };
export { LOCALES, isLocale };

export const LANG_KEY = "mapus.lang";
const catalogs = Object.fromEntries(LOCALES.map((l) => [l.code, l.catalog])) as Record<Locale, Catalog>;

let current: Locale = DEFAULT_LOCALE;

export function locale(): Locale {
  return current;
}

/**
 * `?lang=` wins, then the choice saved in localStorage, then the first browser language
 * we speak (`navigator.languages`), then English.
 */
export function resolveLocale(
  search: string,
  stored: string | null,
  browser: readonly string[] = [],
): Locale {
  const query = new URLSearchParams(search).get("lang");
  if (isLocale(query)) return query;
  if (isLocale(stored)) return stored;
  for (const tag of browser) {
    const match = matchLocale(tag);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

function browserLanguages(): string[] {
  try {
    if (navigator.languages?.length) return [...navigator.languages];
    return navigator.language ? [navigator.language] : [];
  } catch {
    return [];
  }
}

export function storeLocale(lang: Locale): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* private mode */
  }
}

export function bootLocale(): Locale {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LANG_KEY);
  } catch {
    stored = null;
  }
  const next = resolveLocale(location.search, stored, browserLanguages());
  const query = new URLSearchParams(location.search).get("lang");
  if (isLocale(query)) storeLocale(query);
  current = next;
  document.documentElement.lang = next;
  return next;
}

/** This page's URL without `?lang=`, so a reload after switching keeps the new choice. */
export function urlWithoutLang(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("lang");
  return url.toString();
}

let switchHandler: (lang: Locale) => void = (lang) => {
  storeLocale(lang);
  location.replace(urlWithoutLang(location.href));
};

/** The app decides how to switch safely (save first, then reload). */
export function setLocaleSwitchHandler(fn: (lang: Locale) => void): void {
  switchHandler = fn;
}

export function requestLocale(lang: Locale): void {
  if (lang === current) return;
  switchHandler(lang);
}

export function isMessageKey(value: string): value is MessageKey {
  return Object.prototype.hasOwnProperty.call(en, value);
}

/** The catalog key whose English text is exactly `text` (server errors reuse catalog copy). */
export function keyForEnglish(text: string): MessageKey | null {
  for (const key of Object.keys(en) as MessageKey[]) {
    if (en[key] === text) return key;
  }
  return null;
}

export function translate(
  lang: Locale,
  key: MessageKey,
  params?: Record<string, string | number>,
): string {
  let resolved: MessageKey = key;
  const count = params?.count;
  if (typeof count === "number") {
    const rule = new Intl.PluralRules(lang).select(count);
    const pluralKey = `${key}.${rule}`;
    if (isMessageKey(pluralKey) && pluralKey in catalogs[lang]) resolved = pluralKey;
  }
  const template = catalogs[lang][resolved] ?? catalogs.en[resolved];
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (token, name: string) => {
    const value = params[name];
    return value == null ? token : String(value);
  });
}

let overrides: Partial<Record<MessageKey, MessageKey>> = {};

/** Swap whole messages for a page that speaks a narrower vocabulary (e.g. the route window). */
export function setMessageOverrides(map: Partial<Record<MessageKey, MessageKey>>): void {
  overrides = { ...map };
}

export function t(key: MessageKey, params?: Record<string, string | number>): string {
  return translate(current, overrides[key] ?? key, params);
}

/** Distance for the editor sheet. Meters under 1 km, kilometers otherwise. */
export function formatDistance(meters: number, lang: Locale = current): string {
  const locale = lang;
  if (meters < 1000) {
    const n = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(meters));
    return `${n} m`;
  }
  const n = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(meters / 1000);
  return `${n} km`;
}

/** A calendar date: "Oct 23, 2026" / "2026. 10. 23.". Unparseable input gives "". */
export function formatDate(iso: string, lang: Locale = current, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(lang, { dateStyle: "medium", timeZone }).format(d);
}

/** Fill `data-i18n`, `data-i18n-placeholder`, and `data-i18n-aria` from the active catalog. */
export function applyStaticCopy(root: ParentNode = document): void {
  document.documentElement.lang = current;
  const title = document.querySelector("title");
  const titleKey = title?.getAttribute("data-i18n");
  if (title && titleKey && isMessageKey(titleKey)) document.title = t(titleKey);

  root.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const key = el.dataset.i18n;
    if (key && isMessageKey(key)) el.textContent = t(key);
  });
  root.querySelectorAll<HTMLElement>("[data-i18n-placeholder]").forEach((el) => {
    const key = el.dataset.i18nPlaceholder;
    if (key && isMessageKey(key) && el instanceof HTMLInputElement) el.placeholder = t(key);
  });
  root.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach((el) => {
    const key = el.dataset.i18nAria;
    if (key && isMessageKey(key)) el.setAttribute("aria-label", t(key));
  });
}
